<script>
  import WordBox from './WordBox.svelte'
  import RecordPanel from './RecordPanel.svelte'
  import { play } from './audio.js'
  import { decodeWord } from './wordEngine.js'
  import { saveRating } from './ratings.js'
  import { ChevronLeft, Mic, Link2, Sparkles, Check, Star, RotateCcw, ThumbsUp, Volume2 } from '@lucide/svelte'

  // The one shared lesson format used by every section (Lessons, Recommended
  // repetition, Tutoring): introduce the new sound(s), practice each word in the
  // monkey-and-parrot WordBox with a Got-it / Again grade, then a stars summary.
  //
  // steps: [{type:'intro', item:{display,sound?,meaning?,code?,letter_names?}}
  //         | {type:'word'|'tricky', text, word:enriched, review?}]
  // onfinish({results:[{step,correct}], accuracy, minutes}) → optional note string;
  //   the parent applies any progress/mastery there. onexit() → Done / back.
  let { title = '', steps = [], backLabel = '‹ Menu', onexit, onfinish, onpracticemore, source = '', child = null } = $props()

  let stepIndex = $state(0)
  let awaitingGrade = $state(false)
  let showRec = $state(false)
  let replayKey = $state(0)
  let attempted = false
  let results = []           // [{ step, correct }] — first grade per word
  let phase = $state('run')  // run | summary
  let summary = $state(null)
  let startMs = Date.now()
  let scored = false          // apply onfinish only once per session (not on replay)
  let addedMore = $state(false)
  let rated = $state(0)       // tutor's pronunciation rating for the current word
  let cvBlend = $state(readCV()) // show the CV (consonant+vowel) blend stitch

  const step = $derived(steps[stepIndex] || null)

  function playItem(item) { if (item && item.code) play('ph:' + item.code) }

  function next() {
    attempted = false; awaitingGrade = false; showRec = false; rated = 0
    if (stepIndex + 1 < steps.length) stepIndex += 1
    else finish()
  }
  function rate(n) {
    rated = n
    const s = steps[stepIndex]
    if (s && s.text) saveRating({ word: s.text, segments: decodeWord(s.text).map((x) => x.code), rating: n, source, child, ts: Date.now() })
  }
  function record(correct) {
    const s = steps[stepIndex]
    if (s && (s.type === 'word' || s.type === 'tricky')) results.push({ step: s, correct })
  }
  function gradeGot() { if (!attempted) record(true); next() } // correct → next word
  // "Again" replays the SAME word; the first Again scores it as needing help once.
  function gradeAgain() { if (!attempted) { record(false); attempted = true } awaitingGrade = false; replayKey += 1 }

  function finish() {
    const acc = results.length ? results.filter((r) => r.correct).length / results.length : 1
    const stars = acc >= 0.9 ? 3 : acc >= 0.75 ? 2 : 1
    const minutes = Math.max(1, Math.round((Date.now() - startMs) / 60000))
    let note = ''
    if (onfinish && !scored) { scored = true; const r = onfinish({ results, accuracy: acc, minutes }); if (typeof r === 'string') note = r; else if (r && r.note) note = r.note }
    summary = { accuracy: acc, stars, note }
    phase = 'summary'
  }
  function practiceMore() { if (onpracticemore) { onpracticemore(results); addedMore = true } }
  function readCV() { try { return localStorage.getItem('cvBlend') === '1' } catch (_) { return false } }
  function toggleCv() { cvBlend = !cvBlend; try { localStorage.setItem('cvBlend', cvBlend ? '1' : '0') } catch (_) {} }
  function again() {
    stepIndex = 0; awaitingGrade = false; showRec = false; attempted = false; replayKey = 0
    results = []; summary = null; addedMore = false; rated = 0; startMs = Date.now(); phase = 'run'
  }
</script>

<div class="session">
  <div class="toolbar">
    <button class="tb" onclick={onexit}><ChevronLeft size={18} />{backLabel.replace('‹ ', '')}</button>
    {#if phase === 'run' && step && (step.type === 'word' || step.type === 'tricky')}
      <button class="tb rec" onclick={() => (showRec = true)} aria-label="Record this word"><Mic size={18} /> Record</button>
      <button class="tb cv" class:on={cvBlend} onclick={toggleCv} aria-label="Edit blend">{#if cvBlend}<Check size={18} /> Done{:else}<Link2 size={18} /> Blend{/if}</button>
    {/if}
    {#if onpracticemore && phase === 'run'}
      <button class="tb more" onclick={practiceMore} disabled={addedMore}>{#if addedMore}<Check size={18} /> Added{:else}<Sparkles size={18} /> Practice more{/if}</button>
    {/if}
  </div>

  {#if showRec && step && step.word}
    <RecordPanel word={step.word} onclose={() => (showRec = false)} />
  {/if}

  {#if phase === 'run' && step}
    <div class="pbar"><span style={`width:${(stepIndex / Math.max(1, steps.length)) * 100}%`}></span></div>
    {#if title}<p class="stitle">{title}</p>{/if}
    {#if step.type === 'intro'}
      <div class="card intro">
        <p class="tag">{step.item.code ? 'New sound' : 'This lesson'}</p>
        <div class="glyph" class:small={String(step.item.display).length > 3}>{step.item.display}</div>
        {#if step.item.sound}<p class="sound">{step.item.sound}</p>{/if}
        {#if step.item.meaning}<p class="meaning">{step.item.meaning}</p>{/if}
        {#if step.item.code}<button class="hear" onclick={() => playItem(step.item)}><Volume2 size={20} /> Hear it</button>{/if}
        {#if step.item.letter_names}<p class="names">Letter{step.item.letter_names.length > 1 ? 's' : ''}: {step.item.letter_names.join(' ')}</p>{/if}
        <button class="go" onclick={next}>Next ›</button>
      </div>
    {:else}
      <div class="practice">
        {#if step.type === 'tricky'}<p class="tag heart">❤ Tricky word — say the sounds you know</p>
        {:else if step.review}<p class="tag">🔁 Review</p>
        {:else}<p class="tag">Read it</p>{/if}
        {#key `${stepIndex}-${replayKey}`}
          <WordBox word={step.word} {cvBlend} onblendsaved={() => (cvBlend = false)} oncomplete={() => (awaitingGrade = true)} />
        {/key}
        {#if awaitingGrade}
          <div class="grade">
            <button class="again" onclick={gradeAgain}><RotateCcw size={20} /> Again</button>
            <button class="got" onclick={gradeGot}><ThumbsUp size={20} /> Got it</button>
          </div>
        {:else}
          <p class="dim">tap each sound, then tap the word</p>
        {/if}
        <div class="rate">
          <span class="rlabel">Sound:</span>
          {#each [1, 2, 3, 4, 5] as n}
            <button class="star" onclick={() => rate(n)} aria-label={`Rate pronunciation ${n} of 5`}><Star size={24} color="#ffd24d" fill={rated >= n ? '#ffd24d' : 'transparent'} /></button>
          {/each}
        </div>
      </div>
    {/if}
  {:else if phase === 'summary' && summary}
    <div class="card">
      <div class="stars">{#each [1, 2, 3] as n}<Star size={42} color="#ffd24d" fill={summary.stars >= n ? '#ffd24d' : 'transparent'} strokeWidth={2} />{/each}</div>
      <h2>{Math.round(summary.accuracy * 100)}%</h2>
      {#if summary.note}<p class="win">{summary.note}</p>{/if}
      <div class="row">
        <button class="ghost" onclick={again}>Again</button>
        {#if onpracticemore}<button class="more" onclick={practiceMore} disabled={addedMore}>{#if addedMore}<Check size={18} /> Added{:else}<Sparkles size={18} /> Practice more{/if}</button>{/if}
        <button class="go" onclick={onexit}>Done</button>
      </div>
    </div>
  {/if}
</div>

<style>
  .session { position: fixed; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2vh; padding: 7vh 2vh 4vh; box-sizing: border-box; overflow: hidden; }
  .toolbar { position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; display: flex; gap: 1vh; }
  .tb { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0,0,0,0.15); cursor: pointer; display: inline-flex; align-items: center; gap: 0.5vh; }
  .tb.rec { color: #b3271b; }
  .tb.cv { color: #ff7043; }
  .tb.cv.on { background: #ff7043; color: #fff; }
  .tb.more { background: #ffd24d; color: #7a4a00; }
  .tb.more:disabled { opacity: 0.6; cursor: default; }
  .tb:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0,0,0,0.15); }
  .pbar { position: fixed; top: 0; left: 0; right: 0; height: 0.8vh; background: rgba(255,255,255,0.35); }
  .pbar span { display: block; height: 100%; background: #ffd24d; transition: width 0.3s ease; }
  .stitle { margin: 0; color: #fffef0; font-weight: 800; font-size: 2vh; text-shadow: 0 2px 0 rgba(0,0,0,0.15); text-align: center; }
  .card { background: #fffef0; border-radius: 22px; padding: 4vh 5vh; text-align: center; box-shadow: 0 14px 34px rgba(0,0,0,0.22); display: flex; flex-direction: column; align-items: center; gap: 1.4vh; max-width: 92vw; }
  .go { border: none; background: #ff7043; color: #fff; font-family: inherit; font-weight: 800; font-size: 2.4vh; padding: 1.3vh 3vh; border-radius: 999px; box-shadow: 0 5px 0 rgba(0,0,0,0.18); cursor: pointer; }
  .hear, .got, .again, .more { display: inline-flex; align-items: center; gap: 0.6vh; }
  .go:active { transform: translateY(2px); box-shadow: 0 3px 0 rgba(0,0,0,0.18); }
  .ghost { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 2vh; padding: 1.3vh 2.4vh; border-radius: 999px; box-shadow: 0 4px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .intro .glyph { font-size: 16vh; font-weight: 900; color: #2e5d1f; line-height: 1; }
  .intro .glyph.small { font-size: 4.2vh; max-width: 84vw; }
  .tag { margin: 0; color: #4a7a34; font-weight: 800; font-size: 2vh; }
  .tag.heart { color: #c0392b; }
  .sound { margin: 0; color: #ff7043; font-weight: 800; font-size: 3vh; }
  .meaning { margin: 0; color: #4a7a34; font-weight: 700; font-size: 2vh; }
  .names { margin: 0; color: #4a7a34; font-weight: 700; }
  .hear { border: none; background: #ffd24d; color: #7a4a00; font-family: inherit; font-weight: 800; font-size: 2.1vh; padding: 1vh 2.2vh; border-radius: 999px; box-shadow: 0 4px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .practice { display: flex; flex-direction: column; align-items: center; gap: 1.6vh; }
  .grade { display: flex; gap: 2vh; }
  .got { border: none; background: #2e5d1f; color: #fffef0; font-family: inherit; font-weight: 800; font-size: 2.2vh; padding: 1.3vh 2.8vh; border-radius: 999px; box-shadow: 0 5px 0 rgba(0,0,0,0.2); cursor: pointer; }
  .again { border: none; background: #ffdad6; color: #b3271b; font-family: inherit; font-weight: 800; font-size: 2.2vh; padding: 1.3vh 2.8vh; border-radius: 999px; box-shadow: 0 5px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .got:active, .again:active { transform: translateY(2px); }
  .dim { color: #fffef0; font-weight: 700; opacity: 0.9; }
  .rate { display: flex; align-items: center; gap: 0.3vh; }
  .rlabel { color: #fffef0; font-weight: 700; font-size: 1.6vh; margin-right: 0.6vh; opacity: 0.85; }
  .star { border: none; background: transparent; cursor: pointer; padding: 0 0.2vh; display: inline-flex; align-items: center; }
  .stars { display: inline-flex; gap: 0.6vh; align-items: center; }
  .win { margin: 0; color: #2e5d1f; font-weight: 800; font-size: 2.2vh; }
  .row { display: flex; gap: 1.6vh; flex-wrap: wrap; justify-content: center; }
  .more { border: none; background: #ffd24d; color: #7a4a00; font-family: inherit; font-weight: 800; font-size: 2vh; padding: 1.3vh 2.4vh; border-radius: 999px; box-shadow: 0 4px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .more:disabled { opacity: 0.6; cursor: default; }
  .more:active:not(:disabled) { transform: translateY(2px); box-shadow: 0 2px 0 rgba(0,0,0,0.15); }
</style>
